import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Target, Cpu, Users, Briefcase, ArrowRight, CheckCircle } from 'lucide-react';

const Expertise = () => {
  const { language } = useLanguage();

  const expertiseAreas = [
    {
      id: 'sourcing',
      icon: Target,
      title: language === 'fr' ? "Sourcing & IT Strategy" : "Sourcing & IT Strategy",
      subtitle: language === 'fr' 
        ? "Concevoir des modèles opérationnels et écosystèmes fournisseurs qui fonctionnent"
        : "Designing operating models and vendor ecosystems that work",
      description: language === 'fr'
        ? "De nombreuses initiatives de sourcing échouent sur les fondamentaux. Les organisations réussies se concentrent sur la clarté opérationnelle et la maturité du partenariat. Notre accompagnement inclut :"
        : "Many sourcing initiatives fail on fundamentals. Successful organizations focus on operating clarity and partnership maturity. Our support includes:",
      services: [
        {
          title: language === 'fr' ? "Target Operating Model (TOM)" : "Target Operating Model (TOM)",
          description: language === 'fr'
            ? "Principes fondateurs de services IT alignés avec la stratégie business."
            : "Foundational principles of IT services aligned with business strategy."
        },
        {
          title: language === 'fr' ? "Stratégie Sourcing" : "Sourcing Strategy",
          description: language === 'fr'
            ? "Définition des principes de sourcing, paysage fournisseurs et segmentation des services."
            : "Definition of sourcing principles, vendor landscape and service segmentation."
        },
        {
          title: language === 'fr' ? "Négociation & Structuration contractuelle" : "Negotiation & Contract Structuring",
          description: language === 'fr'
            ? "Processus d'évaluation structurés des fournisseurs ancrés dans les réalités opérationnelles."
            : "Structured vendor evaluation processes grounded in operational realities."
        },
        {
          title: language === 'fr' ? "Vendor Management Office (VMO)" : "Vendor Management Office (VMO)",
          description: language === 'fr'
            ? "Visibilité et gouvernance continues de la performance contractuelle."
            : "Ongoing visibility and governance of contractual performance."
        },
        {
          title: language === 'fr' ? "Programmes Carve-In / Carve-Out" : "Carve-In / Carve-Out Programs",
          description: language === 'fr'
            ? "Accompagnement opérationnel lors de transformations structurelles majeures."
            : "Operational support during major structural transformations."
        }
      ],
      philosophy: {
        title: language === 'fr' ? "Notre philosophie sourcing" : "Our sourcing philosophy",
        points: language === 'fr' ? [
          "Un fournisseur ne se choisit pas sur papier — il se révèle dans l'interaction.",
          "La due diligence doit être expérientielle et ancrée dans la réalité du client.",
          "Les signaux humains et systémiques prédisent le succès à long terme mieux que les présentations.",
          "Elle élève l'organisation du client tout en s'adaptant à son contexte."
        ] : [
          "A vendor is not chosen on paper — it is revealed in interaction.",
          "Due diligence must be experiential and grounded in the client's reality.",
          "Human and systemic signals predict long-term success better than presentations.",
          "It elevates the client organization while adapting to its context."
        ]
      },
      color: '#E5A81D'
    },
    {
      id: 'transition',
      icon: Briefcase,
      title: language === 'fr' ? "Management de Transition" : "Transition Management",
      subtitle: language === 'fr'
        ? "Sécuriser la phase la plus critique de l'outsourcing"
        : "Securing the most critical phase of outsourcing",
      description: language === 'fr'
        ? "La phase de transition définit le ton pour l'ensemble du contrat d'outsourcing. Walaby s'assure que les clients obtiennent stabilité, clarté et confiance entre les partenaires."
        : "The transition phase sets the tone for the entire outsourcing contract. Walaby ensures clients get stability, clarity and trust between partners.",
      services: [
        {
          title: language === 'fr' ? "Pilotage de programme de transition" : "Transition Program Steering",
          description: language === 'fr'
            ? "Gestion bout en bout de la phase de transition outsourcing."
            : "End-to-end management of the outsourcing transition phase."
        },
        {
          title: language === 'fr' ? "Gestion relation client/fournisseur" : "Customer/Supplier Relationship Management",
          description: language === 'fr'
            ? "Stabilisation et optimisation des relations partenariales."
            : "Stabilization and optimization of partner relationships."
        }
      ],
      approach: {
        title: language === 'fr' ? "Notre approche transition" : "Our transition management approach",
        description: language === 'fr'
          ? "Une transition réussit quand les attentes, rôles et limites sont explicites — et vécus."
          : "A transition succeeds when expectations, roles and boundaries are explicit — and lived.",
        points: language === 'fr' ? [
          "Définir la gouvernance avant de conduire la transition",
          "Aligner les parties prenantes côté client et fournisseur",
          "Définir les KPIs de transition au-delà du premier trimestre de stabilité",
          "Gérer les transitions en vagues structurées"
        ] : [
          "Define governance early before driving transition",
          "Align stakeholders across client and vendor teams",
          "Define transition KPIs beyond the first quarter of stability",
          "Manage transitions in structured waves"
        ]
      },
      successFactors: {
        title: language === 'fr' ? "Facteurs clés de succès" : "Key transition success factors",
        points: language === 'fr' ? [
          "Vision intégrée du fonctionnement : l'équipe, les méthodes de travail, la culture et la gouvernance",
          "Gouvernance simple et prévisible : les deux parties doivent être transparentes et connectées",
          "Maturité progressive : reconnaître les états intermédiaires et la capacité d'adaptation continue"
        ] : [
          "Integrated view of operating: the team, ways of working, culture and governance",
          "Simple and predictable governance: both parties must be transparent and connected",
          "Progressive maturity: recognize intermediate states and ability to adapt continuously"
        ]
      },
      color: '#0F172A'
    },
    {
      id: 'transformation',
      icon: Cpu,
      title: language === 'fr' ? "Transformation Management" : "Transformation Management",
      subtitle: language === 'fr'
        ? "Conduire des programmes de transformation complexes de la stratégie à la réalité opérationnelle"
        : "Driving complex transformation programs from strategy to operational reality",
      description: language === 'fr'
        ? "Les grands programmes de transformation — tels que les transformations ERP, plateformes digitales ou changements de modèle opérationnel global — ne sont pas seulement des initiatives technologiques. Ils impliquent de multiples organisations, des décisions radicales, et créent des ondes de choc à travers les écosystèmes."
        : "Large transformation programs — such as ERP transformations, digital platforms or global operating model changes — are not simply technology initiatives. They involve multiple organizations, radical decisions, and create shockwaves across ecosystems.",
      support: language === 'fr'
        ? "Walaby accompagne les organisations dans la structuration, l'orchestration et le pilotage de programmes de transformation complexes, en s'assurant que l'ambition stratégique se traduit en réalité opérationnelle mesurable."
        : "Walaby supports organizations in structuring, orchestrating and steering complex transformation programs, ensuring that strategic ambition translates into measurable operational reality.",
      services: [
        {
          title: language === 'fr' ? "Global Transformation Office" : "Global Transformation Office",
          description: language === 'fr'
            ? "Mise en place et animation de bureaux de transformation globaux."
            : "Setup and management of global transformation offices."
        },
        {
          title: language === 'fr' ? "Program Management" : "Program Management",
          description: language === 'fr'
            ? "Pilotage bout en bout de programmes de transformation complexes."
            : "End-to-end management of complex transformation programs."
        },
        {
          title: language === 'fr' ? "Delivery Management" : "Delivery Management",
          description: language === 'fr'
            ? "Coordination des livrables et jalons de transformation."
            : "Coordination of deliverables and transformation milestones."
        },
        {
          title: language === 'fr' ? "Alignement GBPO et IT SME" : "GBPO and IT SME Alignment",
          description: language === 'fr'
            ? "Alignement des équipes business et IT sur les objectifs de transformation."
            : "Alignment of business and IT teams on transformation objectives."
        }
      ],
      capabilities: {
        title: language === 'fr' ? "Nos capacités transformation" : "Our Transformation Capabilities",
        sections: [
          {
            title: language === 'fr' ? "Gouvernance Programme" : "Program Governance",
            description: language === 'fr'
              ? "Nous concevons des structures de gouvernance qui permettent une coordination efficace entre parties prenantes et partenaires : organes de décision clairs, mécanismes d'escalade, reporting standardisé."
              : "We design governance structures that enable effective coordination across stakeholders and partners: clear decision bodies, escalation mechanisms, standardized reporting."
          },
          {
            title: language === 'fr' ? "Pilotage Factuel du Programme" : "Fact-Based Program Steering",
            description: language === 'fr'
              ? "Les programmes de transformation doivent être pilotés par des indicateurs objectifs et un reporting transparent : KPIs, dashboards performance, cadence prédictive."
              : "Transformation programs must be managed through objective indicators and transparent reporting: Program KPIs, performance dashboards, predictive cadence."
          },
          {
            title: language === 'fr' ? "Gestion Périmètre & Livrables" : "Scope & Deliverables Management",
            description: language === 'fr'
              ? "Les transformations complexes nécessitent une définition de périmètre claire et une gestion disciplinée du delivery : propriété claire des rôles, dépendances structurées, planification prévisible."
              : "Complex transformations require clear scope definition and disciplined delivery management: clear accountability and role definition, structured deliverables and acceptance processes, predictable planning and execution."
          }
        ]
      },
      framework: {
        title: language === 'fr' ? "Walaby Transformation Framework" : "Walaby Transformation Framework",
        description: language === 'fr'
          ? "Pour les transformations complexes, Walaby utilise un framework de gestion de transformation intégré couvrant les dimensions clés :"
          : "For complex transformations, Walaby uses an integrated transformation management framework covering key dimensions:",
        dimensions: language === 'fr' ? [
          "Scope Management — s'assurer que les objectifs de transformation restent réalistes et alignés avec les priorités business",
          "Delivery Management — coordonner les planning détaillés et assurer un enchaînement réaliste des jalons",
          "Time Management — s'assurer que les jalons restent prédictibles et atteignables",
          "Risk Management — identifier proactivement et atténuer les obstacles au programme",
          "Resource Management — mobiliser les bonnes capacités au bon moment",
          "Communication Management — maintenir l'alignement des parties prenantes à travers le programme"
        ] : [
          "Scope Management — ensuring transformation objectives remain realistic and aligned with business priorities",
          "Delivery Management — coordinating detailed schedules and ensuring realistic sequencing",
          "Time Management — ensuring milestones remain predictable and achievable",
          "Risk Management — proactively identifying and mitigating obstacles to the program",
          "Resource Management — mobilizing the right capabilities at the right time",
          "Communication Management — maintaining stakeholder alignment across the program"
        ]
      },
      color: '#E5A81D'
    },
    {
      id: 'change',
      icon: Users,
      title: language === 'fr' ? "People, Change & Leadership Coaching" : "People, Change & Leadership Coaching",
      subtitle: language === 'fr'
        ? "Rendre la transformation durable"
        : "Making transformation sustainable",
      description: language === 'fr'
        ? "Les transformations technologiques sont in fine des transformations organisationnelles. De nombreuses initiatives échouent non pas à cause de la complexité technique, mais à cause de : rôles et responsabilités flous, engagement limité des parties prenantes, alignement exécutif et du leadership insuffisant."
        : "Technology transformations are ultimately organizational transformations. Many initiatives fail not because of technical complexity but because of: unclear roles and responsibilities, limited stakeholder engagement, executive and leadership alignment gaps.",
      support: language === 'fr'
        ? "Walaby intègre les dimensions engagement organisationnel et leadership dans les programmes de transformation pour assurer une adoption durable."
        : "Walaby integrates organizational engagement and leadership dimensions into transformation programs to ensure sustainable adoption.",
      services: [
        {
          title: language === 'fr' ? "Design Organisationnel" : "Organizational Design",
          description: language === 'fr'
            ? "Conception de structures organisationnelles alignées avec les objectifs de transformation."
            : "Design of organizational structures aligned with transformation objectives."
        },
        {
          title: language === 'fr' ? "Stratégie de Change Management" : "Change Management Strategy",
          description: language === 'fr'
            ? "Définition et exécution de stratégies de conduite du changement."
            : "Definition and execution of change management strategies."
        },
        {
          title: language === 'fr' ? "Alignement des parties prenantes" : "Stakeholder Alignment",
          description: language === 'fr'
            ? "Engagement et alignement des parties prenantes clés tout au long du programme."
            : "Engagement and alignment of key stakeholders throughout the program."
        },
        {
          title: language === 'fr' ? "Formation et construction des capacités" : "Training and Capability Building",
          description: language === 'fr'
            ? "Développement des compétences intégrées dans les opérations quotidiennes."
            : "Skills development embedded in daily operations."
        }
      ],
      focus: {
        title: language === 'fr' ? "Notre accompagnement inclut" : "Our support includes",
        points: [
          {
            title: language === 'fr' ? "Alignement Exécutif" : "Executive Alignment",
            description: language === 'fr'
              ? "S'assurer que le leadership partage une vision de transformation claire et cohérente."
              : "Ensuring leadership shares a clear and consistent transformation vision."
          },
          {
            title: language === 'fr' ? "Communication Structurée" : "Structured Communication",
            description: language === 'fr'
              ? "Établir des frameworks de communication transparents qui maintiennent les parties prenantes informées et engagées."
              : "Establishing transparent communication frameworks that keep stakeholders informed and engaged."
          },
          {
            title: language === 'fr' ? "Change Management" : "Change Management",
            description: language === 'fr'
              ? "Accompagner les équipes dans l'adoption de nouvelles méthodes de travail, structures de gouvernance et modèles de prise de décision."
              : "Supporting teams in adopting new ways of working, governance structures and decision-making models."
          }
        ]
      },
      color: '#0F172A'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="expertise-page">
      {/* Hero */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="font-heading text-sm font-semibold tracking-[0.2em] text-[#E5A81D] mb-4 block animate-fade-in-up">
            {language === 'fr' ? 'SAVOIR-FAIRE' : 'KNOW-HOW'}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#0F172A] mb-6 animate-fade-in-up animation-delay-100">
            {language === 'fr' ? 'Nos Expertises' : 'Our Expertise'}
          </h1>
          <p className="font-body text-lg text-slate-600 leading-relaxed max-w-3xl animate-fade-in-up animation-delay-200">
            {language === 'fr'
              ? "Notre framework de conseil accompagne la transformation à travers quatre dimensions complémentaires. Ensemble, ces capacités permettent aux organisations de concevoir, de mettre en œuvre et de maintenir une transformation durable."
              : "Our advisory framework supports transformation across four complementary dimensions. Together, these capabilities allow organizations to design, implement and sustain transformation."}
          </p>
        </div>
      </section>

      {/* Expertise Areas */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="space-y-32">
            {expertiseAreas.map((area, index) => (
              <div 
                key={area.id}
                id={area.id}
                className="scroll-mt-24"
                data-testid={`expertise-${area.id}`}
              >
                {/* Header */}
                <div className="flex items-center space-x-4 mb-8">
                  <div 
                    className="p-4"
                    style={{ backgroundColor: `${area.color}15` }}
                  >
                    <area.icon size={32} style={{ color: area.color }} />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0F172A]">
                      {area.title}
                    </h2>
                    <p className="font-body text-slate-600 mt-1">
                      {area.subtitle}
                    </p>
                  </div>
                </div>

                {/* Description */}
                <p className="font-body text-slate-600 leading-relaxed mb-8 max-w-4xl">
                  {area.description}
                </p>

                {/* Support text if exists */}
                {area.support && (
                  <p className="font-body text-slate-700 font-medium mb-8 max-w-4xl">
                    {area.support}
                  </p>
                )}

                {/* Services Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                  {area.services.map((service, serviceIndex) => (
                    <div 
                      key={serviceIndex}
                      className="bg-white p-6 border border-slate-200 hover:border-[#E5A81D]/50 transition-all duration-300"
                    >
                      <h4 className="font-heading text-lg font-semibold text-[#0F172A] mb-2">
                        {service.title}
                      </h4>
                      <p className="font-body text-sm text-slate-600">
                        {service.description}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Philosophy/Approach Section */}
                {area.philosophy && (
                  <div className="bg-[#0F172A] p-8 mb-8">
                    <h3 className="font-heading text-xl font-semibold text-white mb-4">
                      {area.philosophy.title}
                    </h3>
                    <ul className="space-y-3">
                      {area.philosophy.points.map((point, pointIndex) => (
                        <li key={pointIndex} className="flex items-start space-x-3">
                          <CheckCircle size={18} className="text-[#E5A81D] flex-shrink-0 mt-0.5" />
                          <span className="font-body text-slate-300 text-sm">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {area.approach && (
                  <div className="bg-slate-50 p-8 mb-8">
                    <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-2">
                      {area.approach.title}
                    </h3>
                    <p className="font-body text-slate-600 mb-4">{area.approach.description}</p>
                    <ul className="space-y-2">
                      {area.approach.points.map((point, pointIndex) => (
                        <li key={pointIndex} className="flex items-start space-x-3">
                          <CheckCircle size={18} className="text-[#E5A81D] flex-shrink-0 mt-0.5" />
                          <span className="font-body text-slate-700 text-sm">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {area.successFactors && (
                  <div className="bg-[#E5A81D]/10 p-8 mb-8">
                    <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-4">
                      {area.successFactors.title}
                    </h3>
                    <ul className="space-y-3">
                      {area.successFactors.points.map((point, pointIndex) => (
                        <li key={pointIndex} className="flex items-start space-x-3">
                          <CheckCircle size={18} className="text-[#E5A81D] flex-shrink-0 mt-0.5" />
                          <span className="font-body text-slate-700 text-sm">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Capabilities Section for Transformation */}
                {area.capabilities && (
                  <div className="mb-8">
                    <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-6">
                      {area.capabilities.title}
                    </h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      {area.capabilities.sections.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="bg-white p-6 border border-slate-200">
                          <h4 className="font-heading text-lg font-semibold text-[#E5A81D] mb-3">
                            {section.title}
                          </h4>
                          <p className="font-body text-sm text-slate-600">
                            {section.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Framework for Transformation */}
                {area.framework && (
                  <div className="bg-[#0F172A] p-8 mb-8">
                    <h3 className="font-heading text-xl font-semibold text-white mb-4">
                      {area.framework.title}
                    </h3>
                    <p className="font-body text-slate-300 mb-6">{area.framework.description}</p>
                    <ul className="grid md:grid-cols-2 gap-3">
                      {area.framework.dimensions.map((dimension, dimIndex) => (
                        <li key={dimIndex} className="flex items-start space-x-3">
                          <CheckCircle size={16} className="text-[#E5A81D] flex-shrink-0 mt-0.5" />
                          <span className="font-body text-slate-300 text-sm">{dimension}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Focus Section for Change */}
                {area.focus && (
                  <div className="grid md:grid-cols-3 gap-6">
                    {area.focus.points.map((point, pointIndex) => (
                      <div key={pointIndex} className="bg-slate-50 p-6">
                        <h4 className="font-heading text-lg font-semibold text-[#0F172A] mb-2">
                          {point.title}
                        </h4>
                        <p className="font-body text-sm text-slate-600">
                          {point.description}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            {language === 'fr' ? 'PARLONS DE VOTRE PROJET' : "LET'S TALK ABOUT YOUR PROJECT"}
          </h2>
          <p className="font-body text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Prêt à transformer votre organisation ? Contactez-nous pour discuter de vos besoins.'
              : 'Ready to transform your organization? Contact us to discuss your needs.'}
          </p>
          <Link
            to="/contact"
            data-testid="expertise-cta"
            className="inline-flex items-center justify-center bg-[#E5A81D] text-[#0F172A] px-8 py-4 font-heading text-sm font-bold hover:bg-[#E5A81D]/90 hover:shadow-lg transition-all duration-300"
          >
            {language === 'fr' ? 'Prendre rendez-vous' : 'Book an appointment'}
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Expertise;
